/**
 * WeaveIn.ai Platform — Refer a member to a network org (TRUS-749)
 *
 * Neither referral surface on the member profile could reach another org on the
 * platform: "Outside Referrals" is a free-text log, and the Referrals sub-tab's
 * "Create referral" writes the legacy referrals table, which has no receiver.
 *
 * This modal sends the referral to the chosen org's Referral Inbox
 * (POST /api/network-referrals), where their staff accept or decline it. The org
 * list comes from the backend — only orgs that can receive referrals, never the
 * active org itself.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Send } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { getApiErrorMessage } from '@/utils/apiErrors';
import {
  serviceReferralsAPI,
  type NetworkReferralUrgency,
  type ReferableOrg,
  type ReferToOrgResponse,
  type ServiceType,
} from '@/services/api/serviceReferrals';

interface ReferToNetworkOrgModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: number;
  memberName?: string;
  /** Called after a referral is sent. */
  onReferred?: (result: ReferToOrgResponse) => void;
}

const URGENCIES: NetworkReferralUrgency[] = ['routine', 'urgent', 'emergent'];

/** Options for the org picker: network partners (shared program) first. */
export function orgOptions(orgs: ReferableOrg[], partnerLabel: string, otherLabel: string): SearchableSelectOption[] {
  const sorted = [...orgs].sort(
    (a, b) => Number(b.shares_program) - Number(a.shares_program) || a.name.localeCompare(b.name)
  );
  const anyPartner = sorted.some((o) => o.shares_program);
  return sorted.map((o) => ({
    value: String(o.id),
    label: o.name,
    group: anyPartner ? (o.shares_program ? partnerLabel : otherLabel) : undefined,
  }));
}

/** Options for the service picker, grouped by domain in the order the API returns. */
export function serviceOptions(types: ServiceType[]): SearchableSelectOption[] {
  return types.map((st) => ({
    value: st.code,
    label: st.name,
    group: st.domain_name || undefined,
  }));
}

const ReferToNetworkOrgModal: React.FC<ReferToNetworkOrgModalProps> = ({
  open,
  onOpenChange,
  memberId,
  memberName,
  onReferred,
}) => {
  const { t } = useAppTranslation();

  const [orgs, setOrgs] = useState<ReferableOrg[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [orgId, setOrgId] = useState('');
  const [serviceCode, setServiceCode] = useState('');
  const [urgency, setUrgency] = useState<NetworkReferralUrgency>('routine');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ReferToOrgResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    // Fresh form each time the modal opens.
    setOrgId('');
    setServiceCode('');
    setUrgency('routine');
    setNotes('');
    setSubmitError(null);
    setResult(null);

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([serviceReferralsAPI.listReferableOrgs(), serviceReferralsAPI.getServiceTypes()])
      .then(([o, st]) => {
        if (cancelled) return;
        setOrgs(o || []);
        setServiceTypes(st || []);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const orgOpts = useMemo(
    () =>
      orgOptions(
        orgs,
        t('members.networkReferral.networkPartners', 'Shared programs'),
        t('members.networkReferral.otherOrgs', 'Network partners')
      ),
    [orgs, t]
  );
  const serviceOpts = useMemo(() => serviceOptions(serviceTypes), [serviceTypes]);

  const urgencyLabel = (u: NetworkReferralUrgency) =>
    u === 'routine'
      ? t('members.networkReferral.urgencyRoutine', 'Routine')
      : u === 'urgent'
        ? t('members.networkReferral.urgencyUrgent', 'Urgent')
        : t('members.networkReferral.urgencyEmergent', 'Emergent');

  const canSubmit = !!orgId && !!serviceCode && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await serviceReferralsAPI.referToOrg({
        member_id: memberId,
        receiving_cbo_id: Number(orgId),
        service_type_code: serviceCode,
        urgency_level: urgency,
        notes: notes.trim() || undefined,
      });
      setResult(res);
      onReferred?.(res);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('members.networkReferral.title', 'Refer to a network organization')}</DialogTitle>
          <DialogDescription>
            {memberName
              ? t(
                  'members.networkReferral.descriptionNamed',
                  'Send {{name}}’s referral to another organization’s Referral Inbox. Their staff accept or decline it there.',
                  { name: memberName }
                )
              : t(
                  'members.networkReferral.description',
                  'Send this referral to another organization’s Referral Inbox. Their staff accept or decline it there.'
                )}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="network-referral-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            <p className="font-medium">
              {t('members.networkReferral.sentTo', 'Referral sent to {{org}}', { org: result.receiving_org.name })}
            </p>
            {result.referral_number && (
              <p className="text-sm text-muted-foreground">
                {t('members.networkReferral.referralNumber', 'Referral #{{number}}', {
                  number: result.referral_number,
                })}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              {t(
                'members.networkReferral.awaitingResponse',
                'It is waiting in their Referral Inbox for them to accept or decline.'
              )}
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
            {t('members.networkReferral.loading', 'Loading organizations…')}
          </div>
        ) : loadError ? (
          <p className="py-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {loadError}
          </p>
        ) : orgs.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            {t(
              'members.networkReferral.noOrgs',
              'No organizations can receive referrals yet. An organization appears here once Care Referrals is turned on for it.'
            )}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="network-referral-org">{t('members.networkReferral.organization', 'Organization')}</Label>
              <SearchableSelect
                id="network-referral-org"
                options={orgOpts}
                value={orgId}
                onValueChange={setOrgId}
                placeholder={t('members.networkReferral.chooseOrg', 'Choose an organization')}
                searchPlaceholder={t('members.networkReferral.searchOrgs', 'Search organizations…')}
                emptyText={t('members.networkReferral.noMatches', 'No matches')}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="network-referral-service">{t('members.networkReferral.service', 'Service needed')}</Label>
              <SearchableSelect
                id="network-referral-service"
                options={serviceOpts}
                value={serviceCode}
                onValueChange={setServiceCode}
                placeholder={t('members.networkReferral.chooseService', 'Choose a service')}
                searchPlaceholder={t('members.networkReferral.searchServices', 'Search services…')}
                emptyText={t('members.networkReferral.noMatches', 'No matches')}
              />
            </div>

            <fieldset className="space-y-1.5">
              <legend className="text-sm font-medium">{t('members.networkReferral.urgency', 'Urgency')}</legend>
              <div className="flex gap-2" role="radiogroup">
                {URGENCIES.map((u) => (
                  <Button
                    key={u}
                    type="button"
                    size="sm"
                    role="radio"
                    aria-checked={urgency === u}
                    variant={urgency === u ? 'default' : 'outline'}
                    onClick={() => setUrgency(u)}
                  >
                    {urgencyLabel(u)}
                  </Button>
                ))}
              </div>
            </fieldset>

            <div className="space-y-1.5">
              <Label htmlFor="network-referral-notes">{t('members.networkReferral.notes', 'Notes for the receiving organization')}</Label>
              <Textarea
                id="network-referral-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder={t(
                  'members.networkReferral.notesPlaceholder',
                  'Why you are referring, and anything they should know before they reach out.'
                )}
              />
            </div>

            {submitError && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {submitError}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>{t('members.networkReferral.done', 'Done')}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                {t('members.networkReferral.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="mr-1 h-4 w-4" aria-hidden="true" />
                )}
                {t('members.networkReferral.send', 'Send referral')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReferToNetworkOrgModal;
